import { ProjectIssuanceConstants } from "../../../../constants";
import { ComponentProperties } from "../../../../route/shared/projects/project/overview/components/StandardSpecificSection/types";
import { RestorationUnitTotals } from "../../../../utils";
import { Column, Row } from "../../../layout";
import { BNGUnitsKPI } from "../BNGUnitsKPI";
import { NutrientUnitsKPI } from "../NutrientUnitsKPI";
import { ProjectPhaseKPI } from "../ProjectPhaseKPI";
import { RestorationUnitsKPI } from "../RestorationUnitsKPI";
import { TotalCarbonImpactKPI } from "../TotalCarbonImpactKPI";
import { VerifiedPendingKPI } from "../VerifiedPendingKPI";

interface KPIGroupProps {
  projectPhase: string | null;
  projectStatus: string;
  carbonImpactType: ComponentProperties | undefined;
  carbonImpactValue: number;
  unitMeasurement: string;
  areaHabitatUnits: number;
  hedgerowUnits: number;
  watercourseUnits: number;
  nitrogenUnits: number;
  phosphorusUnits: number;
  standardUuid: string;
  hasVeifiedPendingKPI?: boolean;
  verifiedUnits: number;
  pendingUnits: number;
  /** Restore 30x30 totals summed from the project's issuances. Only read for Restoration. */
  restorationUnits?: RestorationUnitTotals;
  /** The listing's "available for sale" toggle; off shows the Restoration total with no split. */
  showAvailable?: boolean;
}

export const KPIGroup = ({
  projectPhase,
  projectStatus,
  carbonImpactType,
  carbonImpactValue,
  unitMeasurement,
  areaHabitatUnits,
  hedgerowUnits,
  watercourseUnits,
  nitrogenUnits,
  phosphorusUnits,
  standardUuid,
  hasVeifiedPendingKPI = false,
  verifiedUnits,
  pendingUnits,
  restorationUnits,
  showAvailable = false,
}: KPIGroupProps): JSX.Element => {
  // Biodiversity projects show one number per BNG unit type, so the card is only worth a slot
  // once at least one type has units.
  const hasBngUnits =
    unitMeasurement === "Biodiversity" && (areaHabitatUnits > 0 || hedgerowUnits > 0 || watercourseUnits > 0);

  /*
    Nutrient Neutrality shows one combined figure split between its two nutrients, so the card is
    worth a slot once either nutrient has units. Both quantities are non-nullable on the public
    projectDetails result, so a project with only nitrogen arrives as 0 phosphorus, not null, and
    still qualifies - the zero band is part of what the tile says.
  */
  const hasNutrientUnits =
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY &&
    (nitrogenUnits > 0 || phosphorusUnits > 0);

  /*
    Restoration (Restore 30x30, KAN-9935) shows one total split Allocated / Available, once the project
    has any units. Its quantity type is not a carbon one, so its verified/pending figures are always 0
    and it only ever reaches the first row below.
  */
  const hasRestorationUnits =
    unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION &&
    (restorationUnits?.total ?? 0) > 0;

  return (
    <div className="KPIGroup">
      {pendingUnits === 0 && verifiedUnits === 0 ? (
        <Row>
          <Column
            span={
              (carbonImpactType && unitMeasurement === "Carbon") ||
              hasBngUnits ||
              hasNutrientUnits ||
              hasRestorationUnits
                ? 6
                : 12
            }
          >
            <ProjectPhaseKPI status={projectStatus} phase={projectPhase} />
          </Column>
          {carbonImpactType && unitMeasurement === "Carbon" && (
            <Column span={6}>
              <TotalCarbonImpactKPI carbonImpactValue={carbonImpactValue} carbonImpactType={carbonImpactType} />
            </Column>
          )}
          {hasBngUnits && (
            <Column span={6}>
              <BNGUnitsKPI
                areaHabitatUnits={areaHabitatUnits}
                hedgerowUnits={hedgerowUnits}
                watercourseUnits={watercourseUnits}
              />
            </Column>
          )}
          {hasNutrientUnits && (
            <Column span={6}>
              <NutrientUnitsKPI nitrogenUnits={nitrogenUnits} phosphorusUnits={phosphorusUnits} />
            </Column>
          )}
          {hasRestorationUnits && restorationUnits && (
            <Column span={6}>
              <RestorationUnitsKPI
                totalUnits={restorationUnits.total}
                availableUnits={restorationUnits.available}
                showAvailable={showAvailable}
              />
            </Column>
          )}
        </Row>
      ) : (
        <Row>
          <Column span={4}>
            <ProjectPhaseKPI status={projectStatus} phase={projectPhase} />
          </Column>
          <Column span={4}>
            <TotalCarbonImpactKPI carbonImpactValue={carbonImpactValue} carbonImpactType={carbonImpactType} />
          </Column>
          {hasVeifiedPendingKPI && (
            <Column span={4}>
              <VerifiedPendingKPI
                verifiedUnits={verifiedUnits}
                pendingUnits={pendingUnits}
                standardUuid={standardUuid}
              />
            </Column>
          )}
        </Row>
      )}
    </div>
  );
};
